import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { BuilderTab } from './types'

export type PassportLayoutTableViewOrder = {
  row: number
  column: number
  colspan: number
  rowspan: number
  view_type: 'value' | 'table'
  name: string | null
  group_id: number | null
}

export type PassportLayoutFieldPayload = {
  id: number | null
  catalog_objects_type_field_id: number
  table_view_order: PassportLayoutTableViewOrder
  pseudo_sql: string | null
  filter_map: boolean
  filter_search: boolean
  filter_dictionary_id: number | null
}

export type PassportLayoutPayload = {
  field_groups: ObjectTypeGroup[]
  field_layouts: PassportLayoutFieldPayload[]
}

export type NegativeGroupIdMap = Record<string, number>

const isPositiveNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

/**
 * Возвращает стабильный отрицательный id для новой группы в рамках текущей сессии.
 */
export const resolveNegativeGroupId = (
  ids: NegativeGroupIdMap,
  clientTabId: string,
): number => {
  const existing = ids[clientTabId]
  if (existing != null) return existing

  const minId = Object.values(ids).reduce((min, id) => Math.min(min, id), 0)
  const nextId = minId < 0 ? minId - 1 : -1
  ids[clientTabId] = nextId
  return nextId
}

const layoutGroupIdForTab = (
  tab: BuilderTab,
  pendingGroupIds: NegativeGroupIdMap,
): number => {
  if (tab.groupId != null) return tab.groupId
  return resolveNegativeGroupId(pendingGroupIds, tab.id)
}

const normalizeGroupForPayload = (
  group: ObjectTypeGroup,
  catalogTypeId: number,
): ObjectTypeGroup => ({
  ...group,
  catalog_invent_objects_type_id:
    Number(group.catalog_invent_objects_type_id) || catalogTypeId,
  list_invent_object_type_id: group.list_invent_object_type_id ?? null,
})

const tabToGroupPayload = (
  tab: BuilderTab,
  sort: number,
  catalogTypeId: number,
  fieldGroupsById: Map<number, ObjectTypeGroup>,
  pendingGroupIds: NegativeGroupIdMap,
): ObjectTypeGroup => {
  const id = layoutGroupIdForTab(tab, pendingGroupIds)
  const group = fieldGroupsById.get(id)

  return {
    id,
    catalog_invent_objects_type_id:
      Number(group?.catalog_invent_objects_type_id) || catalogTypeId,
    name: tab.label,
    open: group?.open ?? true,
    position: group?.position ?? 'APPEND',
    sort,
    protect: group?.protect,
    is_tab: true,
    mode: group?.mode ?? 'EDITABLE',
    type: group?.type ?? 'GROUP',
    list_invent_object_type_id: group?.list_invent_object_type_id ?? null,
  }
}

const fieldIdForLayout = (item: GridFieldType): number | null => {
  if (item.type === 'heading') return null

  const raw = item.inventory_field_id ?? item.id
  const id = Number(raw)
  return isPositiveNumber(id) ? id : null
}

const groupIdForLayout = (
  item: GridFieldType,
  pendingGroupIds: NegativeGroupIdMap,
): number | null => {
  if (item.builder_tab_ref) {
    return resolveNegativeGroupId(pendingGroupIds, item.builder_tab_ref)
  }

  if (item.group_id != null && Number.isFinite(Number(item.group_id))) {
    return Number(item.group_id)
  }

  return null
}

const fieldLayoutPayload = (
  item: GridFieldType,
  pendingGroupIds: NegativeGroupIdMap,
): PassportLayoutFieldPayload | null => {
  const fieldId = fieldIdForLayout(item)
  if (fieldId == null) return null
  const viewType = item.view_type ?? (item.type === 'table' ? 'table' : 'value')

  return {
    id: item.field_layout_id ?? null,
    catalog_objects_type_field_id: fieldId,
    table_view_order: {
      row: item.row + 1,
      column: viewType === 'table' ? 0 : item.cell + 1,
      colspan: viewType === 'table' ? 1 : item.length,
      rowspan: 1,
      view_type: viewType,
      name: item.name?.trim() || null,
      group_id: groupIdForLayout(item, pendingGroupIds),
    },
    pseudo_sql: item.pseudo_sql?.trim() || null,
    filter_map: Boolean(item.filter_map),
    filter_search: Boolean(item.filter_search),
    filter_dictionary_id: item.filter_dictionary_id ?? null,
  }
}

/**
 * Собирает payload для сохранения layout паспорта в новую runtime-модель.
 */
export const buildPassportLayoutPayload = (params: {
  catalogTypeId: number
  fieldGroups: ObjectTypeGroup[]
  tabs: BuilderTab[]
  gridTemplate: GridFieldType[]
  pendingGroupIds: NegativeGroupIdMap
  deletedGroupIds: number[]
}): PassportLayoutPayload => {
  const deletedIds = new Set(params.deletedGroupIds)
  const fieldGroupsById = new Map(
    params.fieldGroups
      .filter((group) => !deletedIds.has(group.id))
      .map((group): [number, ObjectTypeGroup] => [group.id, group]),
  )

  const tabGroups = params.tabs
    .filter((tab) => !tab.isSynthetic)
    .map((tab, index) =>
      tabToGroupPayload(
        tab,
        index + 1,
        params.catalogTypeId,
        fieldGroupsById,
        params.pendingGroupIds,
      ),
    )

  const tabGroupIds = new Set(tabGroups.map((group) => group.id))
  const otherGroups = params.fieldGroups
    .filter((group) => !deletedIds.has(group.id))
    .filter((group) => !tabGroupIds.has(group.id))
    .filter((group) => !group.is_tab || group.type === 'LIST')
    .map((group) => normalizeGroupForPayload(group, params.catalogTypeId))

  return {
    field_groups: [...otherGroups, ...tabGroups],
    field_layouts: params.gridTemplate
      .map((item) => fieldLayoutPayload(item, params.pendingGroupIds))
      .filter((item): item is PassportLayoutFieldPayload => item != null),
  }
}
